import { usePageProps, withLayouts } from 'react-dx'
import { Trans } from '@lingui/react/macro'

import RootLayout from '../_layout/layouts/RootLayout'
import AntdLayout from '../_layout/layouts/AntdLayout'
import SiteSpecificContextLayout from '../_layout/layouts/SiteSpecificContextLayout'
import { paddingHeaderHeightCls } from '../_layout/Header/constants'
import LayoutsDefaultLayout from '../_layout'
import MusicList from '../_components/MusicList'
import { PageStoreProvider } from '../_shared/formStore'

import { createCoverStore } from './_store'
import CoverForm from './_blocks/CoverForm'

import type { MusicListActionRef } from '../_components/MusicList'
import type { InferGetServerSidePropsType } from 'next'
import type { TNextPagePro } from '@/types/global'

import { getServerSideData } from '@/helpers/server-data'
import { FluentFlex, FluentFlexRoot } from '@/components/FluentFlex'
import { getTadFeatureData } from '@/pages/tad.ai/_services/feature-service'
import { FeatureSeoPage } from '@/pages/tad.ai/_blocks/FeatureSeoPage'


import { cls } from 'tagged-classnames-free';
import { useRef } from 'react';
import { useGlobalAudioPlayer } from '@/hooks/audio/store';
import { useConfig } from '@/contexts/SiteContext/hooks';
const fluentFlexItemClassName = cls`flex w-full overflow-auto xl:flex-1`

export const Page: TNextPagePro<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const {
    seoPageData: { hero },
  } = props
  const musicListRef = useRef<MusicListActionRef>(null)
  const { collapsed } = useGlobalAudioPlayer()

  return (
    <PageStoreProvider createStore={createCoverStore}>
      <FluentFlexRoot itemClassName={fluentFlexItemClassName}>
        <FluentFlex
          className={`min-h-screen flex-col xl:h-screen ${paddingHeaderHeightCls}`}
        >
          <h1
            id='top'
            className='mb-3 ml-8 text-xl font-bold md:mb-0 md:text-3xl md:leading-[64px]'
          >
            {hero?.h1 || <Trans>AI Cover Generator</Trans>}
          </h1>
          <FluentFlex
            className={cls`block flex-col gap-5 xl:flex-row ${!collapsed && 'pb-32 xl:pb-24'}`}
          >
            <CoverForm
              onCreated={() => {
                musicListRef.current?.refetchIfPage1(true)
              }}
            />
            <MusicList
              ref={musicListRef}
              className={`mx-auto h-full xl:max-w-none`}
            />
          </FluentFlex>
        </FluentFlex>
      </FluentFlexRoot>
    </PageStoreProvider>
  )
}

Page.noPageKey = true

const LayoutsPage = withLayouts(
  Page,
  [
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()

      return (
        <LayoutsDefaultLayout
          {...props}
          main={{
            className: cls`bg-f-other-1`,
            includingHeader: true,
          }}
          sideBar={{
            className: '',
          }}
        >
          {props.children}
          <FeatureSeoPage pageData={seoPageData} />
        </LayoutsDefaultLayout>
      )
    },
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()
      const { seoTitle } = useConfig()

      return (
        <RootLayout
          seo={{
            title: seoTitle(seoPageData.title),
            description: seoPageData.description,
          }}
          {...props}
        ></RootLayout>
      )
    },
    (props) => {
      return <SiteSpecificContextLayout {...props} />
    },
    AntdLayout,
  ],
  {
    propertiesHoist: ['noPageKey'],
  },
)

export default LayoutsPage

// eslint-disable-next-line react-refresh/only-export-components
export const getServerSideProps = getServerSideData({
  cacheControl: true,
})(async (hostInfo, ctx, helpers) => {
  const trpcHelpers = await helpers.getTrpcHelpers()
  const locale = ctx?.locale || 'en'
  const url = hostInfo.normalizeUrl().url.pathname

  const [, seoPageData] = await Promise.all([
    // SSR 预取 Style 标签树数据，客户端 useQuery 自动命中缓存
    trpcHelpers.preset.labelsTree.prefetch({ language: locale }),
    getTadFeatureData(url, locale),
  ])

  return {
    trpcState: trpcHelpers.dehydrate(),
    seoPageData,
  }
})
